import React, { useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUiStore } from '@/stores/ui';
import { useTasksStore } from '@/stores/tasks';
import { useValidatorsStore } from '@/stores/validators';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { ToyBrick, BotMessageSquare, ListChecks, CheckCircle } from 'lucide-react';
import { ValidatorProgress, ValidatorResults } from '@/core/state-shape';

type StageStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped';

const StageCard = ({ icon, title, status, progress, children }: { icon: React.ReactNode, title: string, status: StageStatus, progress: number, children?: React.ReactNode }) => {
    const getBadgeVariant = (status: StageStatus) => {
        switch (status) {
            case 'succeeded': return 'default';
            case 'running': return 'secondary';
            case 'failed': return 'destructive';
            default: return 'outline';
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {icon}
                        {title}
                    </div>
                    <Badge variant={getBadgeVariant(status)} className="capitalize">{status}</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <Progress value={status === 'succeeded' ? 100 : progress} className="w-full mb-2" />
                {children && <div className="text-sm text-muted-foreground">{children}</div>}
            </CardContent>
        </Card>
    );
};

export default function ProgressPage() {
    const { taskId } = useParams<{ taskId: string }>();
    const navigate = useNavigate();
    const task = useTasksStore(state => taskId ? state.byId[taskId] : undefined);
    const { progress: allValidatorProgress, results: allValidatorResults } = useValidatorsStore();
    const setHeaderTitle = useUiStore((state) => state.setHeaderTitle);

    useEffect(() => {
        setHeaderTitle('Task Progress');
    }, [setHeaderTitle]);
    
    // --- DERIVED STATE ---

    // 1. Contexting Stage
    const contextingStatus: StageStatus = useMemo(() => {
        if (!task || task.status === 'draft') return 'pending';
        // Assume contexting is done if the task has moved past the draft stage.
        return 'succeeded';
    }, [task]);
    const contextingProgress = contextingStatus === 'succeeded' ? 100 : 0;

    // 2. Generating Stage
    const generatingStatus: StageStatus = useMemo(() => {
        if (!task) return 'pending';
        if (task.status === 'generating') return 'running';
        if (['reviewing', 'validated', 'needs_attention', 'complete', 'merged'].includes(task.status)) return 'succeeded';
        if (['error', 'failed'].includes(task.status)) return 'failed';
        return 'pending';
    }, [task]);

    const generatingProgress = useMemo(() => {
        if (generatingStatus === 'running') return 50; // Indeterminate
        if (generatingStatus === 'succeeded') return 100;
        return 0;
    }, [generatingStatus]);

    // 3. Validating Stage
    const activeValidationRun: ValidatorProgress | undefined = useMemo(() => {
        if (!taskId) return undefined;
        return Object.values(allValidatorProgress)
            .filter(p => p.taskId === taskId && p.status === 'running')
            .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
    }, [allValidatorProgress, taskId]);

    const latestValidationResult: ValidatorResults | undefined = useMemo(() => {
        if (!taskId) return undefined;
        return allValidatorResults.byTask[taskId];
    }, [allValidatorResults.byTask, taskId]);

    const validatingStatus: StageStatus = useMemo(() => {
        if (activeValidationRun) return 'running';
        if (latestValidationResult) {
            const hasFailures = (latestValidationResult.lint?.failed ?? 0) > 0 ||
                                (latestValidationResult.typecheck?.failed ?? 0) > 0 ||
                                (latestValidationResult.tests?.failed ?? 0) > 0;
            return hasFailures ? 'failed' : 'succeeded';
        }
        // If generation succeeded but no validation started, it could be skipped or just pending
        if (generatingStatus === 'succeeded') return 'pending'; 
        return 'pending';
    }, [activeValidationRun, latestValidationResult, generatingStatus]);

    const validatingProgress = useMemo(() => {
        if (activeValidationRun) return activeValidationRun.percent ?? 50; // Use percentage, or 50 for indeterminate
        if (validatingStatus === 'succeeded' || validatingStatus === 'failed') return 100;
        return 0;
    }, [activeValidationRun, validatingStatus]);

    const isReviewReady = generatingStatus === 'succeeded';

    if (!taskId) {
        return (
            <div className="p-4 text-center">
                <Card>
                    <CardHeader>
                        <CardTitle>Error</CardTitle>
                        <CardDescription>No Task ID provided in the URL.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button onClick={() => navigate('/pages/tasks')}>Go to Tasks</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }
    return (
        <div className="max-w-3xl mx-auto p-4 space-y-6">
            <header>
                <h1 className="text-2xl font-bold hidden md:block">Task Progress</h1>
                <p className="text-muted-foreground">
                    Tracking generation for: <span className="font-semibold">{task?.title || taskId}</span>
                </p>

            </header>

            <div className="space-y-4">
                <StageCard 
                    icon={<ToyBrick className="h-5 w-5" />} 
                    title="Contexting" 
                    status={contextingStatus} 
                    progress={contextingProgress}
                >
                    <p>Assembling and locking the context bundle for the task.</p>
                </StageCard>

                <StageCard 
                    icon={<BotMessageSquare className="h-5 w-5" />} 
                    title="Generating" 
                    status={generatingStatus} 
                    progress={generatingProgress}
                >
                    <p>Creating a proposal based on the provided context and instructions.</p>
                </StageCard>
                
                <StageCard 
                    icon={<ListChecks className="h-5 w-5" />} 
                    title="Validating" 
                    status={validatingStatus} 
                    progress={validatingProgress}
                >
                    <p>Running lint, type checks, and tests against the generated proposal.</p>
                </StageCard>
            </div>
            
            <footer className="flex justify-end pt-4">
                <Button 
                    size="lg"
                    disabled={!isReviewReady}
                    onClick={() => navigate(`/pages/review/?taskId=${taskId}`)}
                >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    View Review
                </Button>
            </footer>
        </div>
    );
}